import React from "react";
import useDebouncedValue from "hooks/useDebouncedValue";
import type { OrderDto, PreviewOrderPriceDto, PriceBreakdown } from "client/api-v2";
import { previewOrderPrice } from "areas/orders/services/orderService";
import type { OrderQuantityFormRow } from "areas/orders/utils/orderQuantities";
import { validatePricedQuantity } from "areas/orders/utils/orderQuantities";

export const pricePreviewDelayMs = 300;

export interface QuotePricePreview {
  price: PriceBreakdown;
  hasInvalidQuantity: boolean;
  previewFailed: boolean;
}

export function useQuotePricePreview(order: OrderDto, rows: OrderQuantityFormRow[]): QuotePricePreview {
  const hasInvalidQuantity = rows.some((row) => validatePricedQuantity(row.pricedValue) !== true);
  const matchesSaved = rows.every(
    (row) =>
      Number(row.pricedValue.trim()) ===
      order.quantities.find((quantity) => quantity.vector === row.vector)?.pricedValue
  );

  const request =
    hasInvalidQuantity || matchesSaved
      ? undefined
      : JSON.stringify(Object.fromEntries(rows.map((row) => [row.vector, Number(row.pricedValue.trim())])));
  const debouncedRequest = useDebouncedValue(request, pricePreviewDelayMs);

  const [previewed, setPreviewed] = React.useState<PriceBreakdown | undefined>(undefined);
  const [failedRequest, setFailedRequest] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    if (debouncedRequest === undefined) {
      return;
    }
    const controller = new AbortController();
    const quantities: PreviewOrderPriceDto["quantities"] = JSON.parse(debouncedRequest);
    previewOrderPrice(order.odNumber, quantities, controller.signal).then(
      (price) => {
        setPreviewed(price);
        setFailedRequest(undefined);
      },
      () => {
        if (!controller.signal.aborted) {
          setFailedRequest(debouncedRequest);
        }
      }
    );
    return () => controller.abort();
  }, [debouncedRequest, order.odNumber]);

  return {
    price: matchesSaved && !hasInvalidQuantity ? order.price : previewed ?? order.price,
    hasInvalidQuantity,
    previewFailed: request !== undefined && failedRequest === request,
  };
}
